"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Palette, Code, Megaphone, Play, Music, Calculator, Heart, Database, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getPopularCategories } from "../../apis/public";
import { useNavigate } from "react-router-dom";

const PopularCategories = () => {
  const { t, i18n } = useTranslation("home");
  const navigate = useNavigate();

  // Icons mapping
  const icons = [Palette, Code, Megaphone, Play, Music, Calculator, Heart, Database];

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getPopularCategories();
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const getCategoryName = (cat) => {
    if (i18n.language === 'fr' && cat.name_fr) return cat.name_fr;
    if (i18n.language === 'en' && cat.name_en) return cat.name_en;
    if (i18n.language === 'ar' && cat.name_ar) return cat.name_ar;
    return cat.name; // Fallback
  };

  const displayCategories = categories.map((cat, idx) => ({
    title: getCategoryName(cat),
    positions: cat.count
  }));

  if (displayCategories.length === 0) return null;

  return (
    <section className="py-24 bg-gray-50 dark:bg-gray-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
              {t('popularCategories.title')} <span className="text-yellow-500">{t('popularCategories.titleHighlight')}</span>
            </h2>
            <p className="text-gray-500 dark:text-gray-400">{t('popularCategories.subtitle')}</p>
          </div>
          <button onClick={() => navigate('/job-offers')} className="hidden md:flex items-center gap-2 text-gray-900 dark:text-white font-medium hover:gap-3 transition-all">
            {t('popularCategories.browseAll')} <ArrowRight size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayCategories.map((cat, idx) => {
            const Icon = icons[idx % icons.length] || Palette;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -5 }}
                className="group p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 hover:border-yellow-400/50 hover:shadow-lg dark:hover:shadow-yellow-900/10 transition-all cursor-pointer"
                onClick={() => navigate(`/job-offers?search=${encodeURIComponent(cat.title)}`)} // Basic filtering
              >
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/10 text-primary-600 dark:text-primary-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                  <Icon size={24} />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-100 transition-colors">
                  {cat.title}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {cat.positions} {t('popularCategories.openPositions')}
                </p>
              </motion.div>
            );
          })}
        </div>

        <button onClick={() => navigate('/job-offers')} className="md:hidden mt-8 w-full py-4 flex items-center justify-center gap-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 font-medium">
          {t('popularCategories.browseAll')} <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
};

export default PopularCategories;